import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from './Card'
import { Dialog } from './Dialog'
import { MyQuestions } from './SupportInbox'
import {
  Emph,
  IntentChoices,
  NoPrivateHrRoute,
  RouteQuestionFields,
  RouteSent,
  useRouting,
} from './SupportSend'
import { type Me } from '../lib/me'
import { useHubLang } from '../lib/i18n'
import { ASK, GUIDE_LINKS, HELP } from '../lib/hubCopy'
import { GuideLink } from './guides/GuideLink'
import { useDismiss } from '../lib/useDismiss'
import { useOpenLanes } from '../lib/openLanes'
import { laneWords, loadMySupportQuestions, type MySupportQuestion } from '../lib/support'
import { OFFERED_INTENTS, SOME_INTENT_HELD, askIntent, intentRouting } from '../lib/wiki'
import { seedIntent, type HelpSeed } from '../lib/helpLink'
import { loadGuides } from '../lib/guides'

/**
 * #/help's body (337, HLP-43; One and Help v2 S1). In components/, not the
 * route file, so the render suite can mount it: the route imports AppShell,
 * and AppShell an SVG the test bundler cannot resolve.
 *
 * `onAskOne` is handed in by the route's child (useAskOne() must be called
 * BELOW the shell's provider - #/wiki's five days of a dead button).
 */
/** The page itself; the suite mounts it with data. */
export function HelpPage({
  me,
  onAskOne,
  initialTickets,
  initialGuideCount,
  seed = null,
  onSeedUsed,
}: {
  me: Me
  onAskOne: () => void
  /** The suite's way in; the page loads its own otherwise. */
  initialTickets?: { rows: MySupportQuestion[]; total: number }
  /** 339. How many guides are yours; the page asks guides() otherwise. */
  initialGuideCount?: number
  /**
   * S2 (31 Oct, HLP-44). A prefilled link's request, already validated by
   * `parseHelpSeed` (lib/helpLink.ts): which form to open, the team, the
   * words. The form opens FILLED and is the person's to change or cancel -
   * nothing is sent by arriving here. Ask One's "Need a person?" and its
   * idea/fault line are links of this shape (HLP-41 kept as a link).
   */
  seed?: HelpSeed | null
  /** Called when a seeded form closes, so the route can drop the URL's
   *  request and a reload does not open it again. */
  onSeedUsed?: () => void
}) {
  const lang = useHubLang()
  const t = HELP[lang]
  const gl = GUIDE_LINKS[lang]
  const openAsk = onAskOne
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<'ticket' | 'feedback' | null>(seed?.open ?? null)
  // The seed belongs to the FIRST opening only. A person who closes the
  // filled form and opens New ▸ Ticket again gets a blank one.
  const [pending, setPending] = useState<HelpSeed | null>(seed)
  const closeDialog = () => {
    setDialog(null)
    if (pending) {
      setPending(null)
      onSeedUsed?.()
    }
  }
  const [rows, setRows] = useState<MySupportQuestion[]>(initialTickets?.rows ?? [])
  const [total, setTotal] = useState(initialTickets?.total ?? 0)

  const reload = useCallback(() => {
    loadMySupportQuestions()
      .then((page) => {
        setRows(page.rows)
        setTotal(page.totalMatches)
      })
      // An empty list is a TRUE statement about you (MyQuestions), so a failed
      // read leaves what was there rather than inventing an error state.
      .catch(() => undefined)
  }, [])
  useEffect(() => {
    if (!initialTickets) reload()
  }, [reload, initialTickets])

  const [guideCount, setGuideCount] = useState<number | null>(initialGuideCount ?? null)
  useEffect(() => {
    if (initialGuideCount !== undefined) return
    // 349: every readable guide comes back; the count is the ones FOR you.
    loadGuides(me.locale)
      .then((g) => setGuideCount(g.filter((x) => x.yours).length))
      .catch(() => setGuideCount(null))
  }, [initialGuideCount, me.locale])

  return (
    <div className="helppage">
      <section className="card helppage__top">
        <p className="helppage__lede">{t.lede}</p>
        <NewMenu
          onAskOne={() => openAsk()}
          onTicket={() => setDialog('ticket')}
          onFeedback={() => setDialog('feedback')}
        />
        {/* 353 (HLP-50): the ticket and feedback forms are dialogs, so their
            guides sit here beside New, never inside the dialog. */}
        <p className="helppage__guides">
          <GuideLink guide="getting-help" section="ticket" label={gl.ticket} />
          <GuideLink guide="getting-help" section="feedback" label={gl.feedback} />
        </p>
      </section>

      <div className="helppage__cards">
        <Card title={t.guidesTitle}>
          <p className="helppage__body">{t.guidesBody}</p>
          <div className="helppage__links">
            <button type="button" className="btn btn--sm btn--ai" onClick={() => openAsk()}>
              {t.guidesAsk}
            </button>
            {/* 339 (S3): one link to #/help/guides, with the SERVER's count of
                the guides that are yours - guides() decides, nothing here
                re-judges a role. Unknown (still loading, or the read failed)
                shows the link without a number rather than a wrong one. */}
            <a className="btn btn--quiet btn--sm" href="#/help/guides">{t.guidesOpen}</a>
          </div>
          {guideCount !== null && guideCount > 0 && (
            <p className="helppage__count">{t.guidesCount(guideCount)}</p>
          )}
        </Card>
        <Card title={t.wikiTitle}>
          <p className="helppage__body">{t.wikiBody}</p>
          <div className="helppage__links">
            <a className="btn btn--quiet btn--sm" href="#/wiki">{t.wikiOpen}</a>
          </div>
        </Card>
      </div>

      <Card title={t.ticketsTitle}>
        <p className="supportinbox__lede">{t.ticketsLede}</p>
        <GuideLink guide="getting-help" section="your-tickets" label={gl.yourTickets} />
        <MyQuestions rows={rows} totalMatches={total} onOpen={(id) => navigate(`/support/${id}`)} />
      </Card>

      <TicketDialog
        open={dialog === 'ticket'}
        onClose={closeDialog}
        onSent={reload}
        onAskOne={() => {
          closeDialog()
          openAsk()
        }}
        seed={pending?.open === 'ticket' ? pending : null}
      />
      <FeedbackDialog
        open={dialog === 'feedback'}
        onClose={closeDialog}
        onSent={reload}
        seed={pending?.open === 'feedback' ? pending : null}
      />
    </div>
  )
}

/**
 * New ▾ — three things, Ask One first. A disclosure, not a select: each row
 * opens something, and the line under each says what you get.
 */
export function NewMenu({
  onAskOne,
  onTicket,
  onFeedback,
}: {
  onAskOne: () => void
  onTicket: () => void
  onFeedback: () => void
}) {
  const t = HELP[useHubLang()]
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  useDismiss(wrapRef, open, (viaKeyboard) => {
    setOpen(false)
    if (viaKeyboard) buttonRef.current?.focus()
  })
  const pick = (act: () => void) => () => {
    setOpen(false)
    act()
  }
  return (
    <div className="helpnew" ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className="btn btn--primary btn--sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t.newMenuLabel}
        onClick={() => setOpen(!open)}
      >
        + {t.newLabel} ▾
      </button>
      {open && (
        <ul className="helpnew__panel" role="menu">
          <li role="none">
            <button type="button" role="menuitem" className="helpnew__item helpnew__item--ai" onClick={pick(onAskOne)}>
              <span className="helpnew__name">{t.newAskOne}</span>
              <span className="helpnew__who">{t.newAskOneWho}</span>
            </button>
          </li>
          <li role="none">
            <button type="button" role="menuitem" className="helpnew__item" onClick={pick(onTicket)}>
              <span className="helpnew__name">{t.newTicket}</span>
              <span className="helpnew__who">{t.newTicketWho}</span>
            </button>
          </li>
          <li role="none">
            <button type="button" role="menuitem" className="helpnew__item" onClick={pick(onFeedback)}>
              <span className="helpnew__name">{t.newFeedback}</span>
              <span className="helpnew__who">{t.newFeedbackWho}</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  )
}

/**
 * THE TICKET — the routing step as a form the person chose to open (D2).
 *
 * The lanes offered are the intents that HAVE a lane: the community board is
 * a public post, not help, and stays on Wiki One. HR is held (HLP-20), so it
 * is not offered; `NoPrivateHrRoute` says where an employment question goes,
 * with no button, as it does in the drawer. A lane nobody holds sends nothing
 * and says so — `intentRouting` decides, the same function the send reads.
 */
export function TicketDialog({
  open,
  onClose,
  onSent,
  onAskOne,
  seed = null,
}: {
  open: boolean
  onClose: () => void
  onSent: () => void
  onAskOne: () => void
  /** S2. A prefilled link: its team and words fill the form; Send is still
   *  the person's. */
  seed?: HelpSeed | null
}) {
  const lang = useHubLang()
  const t = HELP[lang]
  const c = ASK[lang]
  const openLanes = useOpenLanes()
  const routing = useRouting(openLanes)
  const { start, reset } = routing

  // Each opening is a fresh ticket, Platform support pre-selected (D2) unless
  // a link named another offered team; a link's words fill it (S2).
  useEffect(() => {
    if (open) start(seedIntent(seed), seed ? { text: seed.text, from: seed.from, page: seed.page } : undefined)
    else reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const lanes = OFFERED_INTENTS.filter((i) => i.lane !== null)
  const route = intentRouting(routing.routeKey, openLanes)
  const toLane = route === 'lane' ? askIntent(routing.routeKey)?.lane ?? null : null

  return (
    <Dialog open={open} title={t.ticketTitle} onClose={onClose}>
      {routing.phase === 'sent' && routing.outcome ? (
        <RouteSent
          outcome={routing.outcome}
          doneLabel={t.done}
          onDone={() => {
            onSent()
            onClose()
          }}
        />
      ) : (
        <div className="oneroute helpform">
          <p className="onemsg__body">
            {t.ticketLede}{' '}
            <button type="button" className="linkbtn" onClick={onAskOne}>
              {t.ticketAskOne}
            </button>
          </p>
          <p className="oneroute__label">{t.whoReads}</p>
          <IntentChoices intents={lanes} selected={routing.routeKey} onPick={routing.setRouteKey} />
          {SOME_INTENT_HELD && <NoPrivateHrRoute />}

          {route === 'fallback' && routing.routeKey && askIntent(routing.routeKey)?.lane && (
            <p className="oneroute__warn">
              {t.laneClosed(laneWords(askIntent(routing.routeKey)!.lane!, lang).queue)}
            </p>
          )}

          {toLane && (
            <RouteQuestionFields
              question={routing.question}
              setQuestion={routing.setQuestion}
              detail={routing.detail}
              setDetail={routing.setDetail}
              toLane={toLane}
              media={routing.media}
              priority={{ value: routing.priority, onPick: routing.setPriority }}
            />
          )}

          {routing.error && <p className="onedrawer__error" role="alert">{routing.error}</p>}
          {/* 348. Said, not just greyed: a disabled Send with no reason reads as
              the form being broken. */}
          {toLane && routing.question.trim() && !routing.priority && (
            <p className="onepics__hint">{t.priorityNeeded}</p>
          )}

          <div className="oneroute__actions">
            <button
              className="btn btn--primary btn--sm"
              type="button"
              onClick={() => void routing.post()}
              disabled={!toLane || routing.posting || !routing.question.trim() || !routing.priority}
            >
              {routing.posting ? c.sending : toLane ? c.sendTo(laneWords(toLane, lang)) : t.newTicket}
            </button>
            <button className="btn btn--quiet btn--sm" type="button" onClick={onClose}>
              {t.cancel}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  )
}

/**
 * FEEDBACK — an idea or a suggestion, to the platform desk (D3). One box. It
 * says who reads it before it goes, like every private send.
 */
export function FeedbackDialog({
  open,
  onClose,
  onSent,
  seed = null,
}: {
  open: boolean
  onClose: () => void
  onSent: () => void
  /** S2. A prefilled link's words (an idea One recognised). Editable. */
  seed?: HelpSeed | null
}) {
  const lang = useHubLang()
  const t = HELP[lang]
  const c = ASK[lang]
  const openLanes = useOpenLanes()
  const routing = useRouting(openLanes)
  const [text, setText] = useState(seed?.text ?? '')
  const { reset } = routing
  useEffect(() => {
    if (open) setText(seed?.text ?? '')
    else {
      reset()
      setText('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  const canSend = intentRouting('platform', openLanes) === 'lane'

  return (
    <Dialog open={open} title={t.feedbackTitle} onClose={onClose}>
      {routing.phase === 'sent' && routing.outcome ? (
        <RouteSent
          outcome={routing.outcome}
          doneLabel={t.done}
          onDone={() => {
            onSent()
            onClose()
          }}
        />
      ) : (
        <div className="oneroute helpform">
          <label className="oneroute__label" htmlFor="help-feedback">
            {t.feedbackLabel}
          </label>
          <textarea
            id="help-feedback"
            className="composer__body"
            rows={5}
            maxLength={2000}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t.feedbackPlaceholder}
          />
          {canSend ? (
            <p className="oneroute__warn">
              <Emph parts={t.feedbackWho} />
            </p>
          ) : (
            <p className="oneroute__warn">{t.feedbackClosed}</p>
          )}
          {routing.error && <p className="onedrawer__error" role="alert">{routing.error}</p>}
          <div className="oneroute__actions">
            <button
              className="btn btn--primary btn--sm"
              type="button"
              onClick={() => void routing.sendFeedback(text, seed?.from)}
              disabled={!canSend || routing.posting || !text.trim()}
            >
              {routing.posting ? c.sending : t.sendFeedback}
            </button>
            <button className="btn btn--quiet btn--sm" type="button" onClick={onClose}>
              {t.cancel}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  )
}
